import { useEffect, useMemo, useState } from "react";
import { api } from "../../../software/api";
import { resolveParams, flattenRange, rangeCatFor } from "./waterChem";
import { recommendations } from "./waterReport";
import { loadLayouts, promoKey } from "./waterLayout";
import { computeMineralDose, isMineralBody } from "./mineralSalt";
import { stepFromRec, mergeSteps } from "./dosing";

// The desktop client Water Test's test list + dosing engine, packaged so Pool-Tech gets identical results.
const apiGet = (p) => api("GET", p);

export function useDosingSetup(fetcher = apiGet) {
  const [s, setS] = useState(null);
  useEffect(() => {
    const safe = (p, d) => fetcher(p).catch(() => d);
    Promise.all([safe("/water-templates", []), safe("/chemical-ranges", []), safe("/dosing-rules", null), safe("/water-test-layout", null), safe("/products", [])])
      .then(([tpl, ranges, rules, layout, products]) => {
        const templatesByType = {}; (tpl || []).forEach((t) => { templatesByType[t.type] = t; });
        const catByName = {}; (products || []).forEach((p) => { if (p.name) catByName[p.name.toLowerCase()] = p; });
        setS({ templatesByType, ranges: ranges || [], rules, layouts: loadLayouts(layout), catByName });
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return s;
}

export function paramsFor(body, setup) {
  return setup ? resolveParams({ type: body?.type, category: body?.category, range_overrides: body?.range_overrides }, setup.templatesByType) : [];
}

export function dosingFor(body, readings, setup, treat = {}) {
  if (!setup || !body) return { steps: [], mineral: { applicable: false } };
  const resolved = paramsFor(body, setup);
  const byName = Object.fromEntries(setup.ranges.map((r) => [r.name, r]));
  const range = byName[body.chemical_brand] || byName.BioGuard || setup.ranges[0] || null;
  const brand = body.chemical_brand || setup.ranges.filter((r) => r.active).map((r) => r.name)[0] || "Aqualux Nexus";
  const productMap = flattenRange(range, body.category) || null;
  const matrix = range?.matrix ? range.matrix[rangeCatFor(body.category)] || null : null;
  const oxidisers = (matrix?.oxidiser || []).map((p) => p.name).filter(Boolean);
  let recs = [];
  try { recs = recommendations(readings, body, resolved, brand, productMap, setup.layouts[promoKey(body)].rec_texts, treat, oxidisers); } catch { recs = []; }
  const steps = mergeSteps([], recs, (r) => stepFromRec(r, matrix, setup.catByName));
  const mineral = isMineralBody(body) ? computeMineralDose({ readings, body, rules: setup.rules }) : { applicable: false };
  return { steps, mineral };
}

export const useDosing = (body, readings, setup, treat) => useMemo(() => dosingFor(body, readings, setup, treat), [body, readings, setup, treat]);
